import React, { useContext, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { convertTimeStringToMoment, DEFAULT_END_TIME, DEFAULT_START_TIME } from '@ubeya/shared/utils/time';
import { getReportMethodLabelKey, isAdminEnteredReport } from '@ubeya/shared/utils/reportMethod';
import { API_DATE_FORMAT, API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import {
  FlexColumn,
  SmallBody,
  smallBody,
  ExtraSmall,
  TimePicker as BaseTimePicker,
  flexMiddle,
  Tooltip
} from '@ubeya/shared-web/components';
import { ReactComponent as BaseErrorIcon } from '@ubeya/shared-web/assets/error.svg';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { DISABLE_FILL_HOURS_BY_JOB_TYPE } from '../../constants';
import ReporterTooltip from './ReporterTooltip';
import { getDistance } from 'timeSheet/utils';

const Root = styled(FlexColumn)`
  width: 100%;
`;

const ActualValue = styled(SmallBody)`
  ${flexMiddle};
  position: relative;
  width: 100%;
`;

const ValueWrapper = styled.div`
  ${flexMiddle};
  min-width: 0;
`;

const IconsWrapper = styled.div`
  ${flexMiddle};
  flex-shrink: 0;
  gap: 2px;
  margin-left: auto;
`;

const TimePicker = styled(BaseTimePicker)`
  .select {
    border: none;
    background: transparent;
    box-shadow: none;
  }

  .select-arrow {
    display: none;
  }

  .clear-icon {
    position: absolute;
    right: 0;
    top: 0;
    border-right: unset;
    border-left: 1px solid ${({ theme }) => theme.colors.grey2};

    width: 10px;
    padding: 0 2px;
    cursor: pointer;
    visibility: hidden;
  }

  .select-input,
  .select-label {
    ${smallBody};
    padding: 0;
    min-height: 22px;
  }
`;

const LabelTime = styled.span`
  color: ${({ theme, hasError }) => hasError && theme.colors.redNegative};
`;

const ExtraDay = styled(ExtraSmall)``;

const ErrorIcon = styled(BaseErrorIcon)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const BaseTimeRenderer = ({
  isIn,
  rowData: { id, subRows, actualIn, branchId, jobTypeId, summarized, isJob, isPto, locationId },
  actualValue,
  actualUserValue,
  actualReporter,
  scheduleValue,
  extraDay,
  onChangeActualValue,
  startTime,
  isEditable,
  showWarning = null,
  disableWarning = false,
  onOutsideClickSave,
  tooltipBody = null,
  reportMethod = null,
  reportedValueMethod = null,
  clearable = false,
  visible = false
}) => {
  const { mappedLocations } = useLocations();
  const { t } = useContext(ConfigContext);
  const { timeFormat } = useContext(ConfigContext);
  const { mappedBranches } = useBranches();

  const compareFormat = 'YYYY-MM-DD HH:mm';

  const isUserTimeDifferActualTime = useMemo(
    () =>
      actualValue &&
      actualUserValue &&
      moment(actualUserValue).format(compareFormat) !== moment(actualValue).format(compareFormat),
    [actualUserValue, actualValue]
  );

  const actualTime = useMemo(() => moment(actualValue).format(timeFormat), [actualValue, timeFormat]);
  const actualUserTime = useMemo(() => (actualUserValue ? moment(actualUserValue).format(timeFormat) : null), [
    actualUserValue,
    timeFormat
  ]);
  const selectedBranch = mappedBranches[branchId];

  const reportedTimeLabel = useMemo(
    () => (isAdminEnteredReport(reportedValueMethod) ? t('adminOriginalHours') : t('userOriginalHours')),
    [reportedValueMethod, t]
  );

  const reportMethodLabel = useMemo(() => {
    if (!actualValue) {
      return null;
    }
    const key = getReportMethodLabelKey(reportMethod);
    return key ? t(key) : null;
  }, [actualValue, reportMethod, t]);

  const withReportMethod = useCallback(
    (base) => {
      if (!reportMethodLabel) {
        return base;
      }
      return (
        <>
          {typeof base === 'string' ? <div>{base}</div> : base}
          <div>{reportMethodLabel}</div>
        </>
      );
    },
    [reportMethodLabel]
  );

  const editableTooltipBody = useMemo(() => withReportMethod(tooltipBody), [withReportMethod, tooltipBody]);

  const readOnlyTooltipBody = useMemo(
    () =>
      withReportMethod(
        selectedBranch?.timesheetConfig?.limitClockInTime
          ? t('alignedTimestampTooltip', { time: actualTime })
          : tooltipBody
      ),
    [withReportMethod, selectedBranch?.timesheetConfig?.limitClockInTime, t, actualTime, tooltipBody]
  );

  const curLocationId = useMemo(() => locationId || selectedBranch?.locationId, [
    locationId,
    selectedBranch?.locationId
  ]);

  const hasPassedMinimumDistanceReport = useMemo(
    () =>
      selectedBranch?.timesheetConfig?.distanceFromShiftAddress &&
      actualReporter?.lat &&
      actualReporter?.lng &&
      mappedLocations[curLocationId]?.lat &&
      mappedLocations[curLocationId]?.lng &&
      Math.abs(getDistance(actualReporter, mappedLocations[curLocationId])) >
        selectedBranch?.timesheetConfig?.distanceFromShiftAddress,
    [actualReporter, curLocationId, mappedLocations, selectedBranch?.timesheetConfig?.distanceFromShiftAddress]
  );

  const defaultTime = useMemo(() => {
    const time = selectedBranch?.timesheetConfig?.[isIn ? 'defaultJobStartTime' : 'defaultJobEndTime'];
    return convertTimeStringToMoment(time ?? (isIn ? DEFAULT_START_TIME : DEFAULT_END_TIME)).format(
      API_DATE_TIME_FORMAT
    );
  }, [isIn, selectedBranch]);

  const isActualStartDateDifferentFromScheduledStartDate = useMemo(() => {
    // Jobs don't have scheduled start
    if (!actualValue || isJob || isPto) {
      return false;
    }
    const actualStartDate = moment(actualValue, API_DATE_TIME_FORMAT).format(API_DATE_FORMAT);
    const scheduledStartDate = moment(scheduleValue, API_DATE_TIME_FORMAT).format(API_DATE_FORMAT);
    return actualStartDate !== scheduledStartDate;
  }, [actualValue, isJob, isPto, scheduleValue]);

  if (summarized || subRows || (!isIn && (!actualIn || id < 0)) || DISABLE_FILL_HOURS_BY_JOB_TYPE[jobTypeId]) {
    return <></>;
  }

  const hasError = !actualValue;

  return (
    <Root>
      <ActualValue className="actual">
        <ValueWrapper>
          {isEditable ? (
            <TimePicker
              value={!actualValue ? undefined : actualValue}
              onChange={onChangeActualValue}
              startTime={startTime}
              placeholder=""
              defaultValue={scheduleValue || defaultTime || actualIn}
              usePortal
              onOutsideClickSave={onOutsideClickSave}
              tooltipBody={editableTooltipBody}
              clearable={clearable}
              visible={visible}
              dataTestId={isIn ? 'clock-in-time-picker' : 'clock-out-time-picker'}
            />
          ) : (
            <Tooltip body={readOnlyTooltipBody}>
              <LabelTime hasError={hasError} data-testid={isIn ? 'clock-in-time-display' : 'clock-out-time-display'}>
                {actualValue ? actualTime : ''}
              </LabelTime>
            </Tooltip>
          )}
          {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
          {extraDay && <ExtraDay>{'(+1)'}</ExtraDay>}
        </ValueWrapper>
        <IconsWrapper>
          {actualReporter?.type && (
            <ReporterTooltip reporter={actualReporter} isJob={isJob} branchId={branchId} locationId={locationId} />
          )}
          {!disableWarning && (!!showWarning || (!isIn && !actualValue)) && (
            <Tooltip body={showWarning || t('noEndTime')}>
              <ErrorIcon />
            </Tooltip>
          )}
          {!disableWarning && !!hasPassedMinimumDistanceReport && (
            <Tooltip body={showWarning || t('hasPassedMinimumDistanceReport')}>
              <ErrorIcon />
            </Tooltip>
          )}
          {!disableWarning && isIn && isActualStartDateDifferentFromScheduledStartDate && (
            <Tooltip body={t('timestampNotOnShiftDay')}>
              <ErrorIcon />
            </Tooltip>
          )}
        </IconsWrapper>
      </ActualValue>
      {isUserTimeDifferActualTime && (
        <Tooltip body={reportedTimeLabel}>
          <div>{actualUserTime}</div>
        </Tooltip>
      )}
    </Root>
  );
};

export default BaseTimeRenderer;
